// External Imports
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';

import Moment from 'moment';

import * as formik from 'formik';
import * as yup from 'yup';

// Components
import ProcessingAlert from './ProcessingAlert';
import RequestingAlert from './alerts/RequestingAlert';

//Hooks
import { useAuth } from 'react-oidc-context';
import useToken from '../utils/useToken';
import useBearerToken from '../utils/useBearerToken';
import { fetchCustomerVouchers, activateVoucher } from '../utils/quota';

//Main View
export default function ActivateVoucher({ show, type, site, closeFunction }) {
  const auth = useAuth();
  const { token, isValidToken } = useToken();
  const { isLoggedIn, getToken } = useBearerToken();

  // Local Data
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [error, setError] = useState({ message: '', severity: 'primary' });

  const [vouchers, setVouchers] = useState([]);

  const schema = yup.object().shape({
    activation_code: yup.string().required(),
  });
  const { Formik } = formik;

  const getAllocatedVouchers = async () => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    setLoading(true);

    fetchCustomerVouchers(bearerToken, site.customer_id)
      .then((data) => {
        setError({ message: '', severity: 'success' });
        const filtered = data.filter((voucher) => {
          return voucher.type === type && voucher.status === 'queued';
        });
        setVouchers(filtered);
        setLoading(false);
      })
      .catch((error) => {
        setError({
          message: 'Could not retrieve vouchers for customer! ' + error,
          severity: 'danger',
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    getAllocatedVouchers();
  }, [site]);

  // Event Handlers
  const handleForm = async (values, { resetForm }) => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    const voucher = {
      activation_code: values.activation_code,
      type: type,
      site_id: site.site_id,
    };

    setRequesting(true);

    activateVoucher(bearerToken, voucher, site.customer_id)
      .then((response) => {
        setError({ message: 'Activation has been requested!', severity: 'success' });
        setRequesting(false);
        setProcessing(true);
      })
      .catch((error) => {
        setError({ message: '' + error, severity: 'danger' });
        setRequesting(false);
      });
  };

  const processed = async (tf) => {
    setProcessing(false);
    closeFunction(true);
  };

  return (
    <Offcanvas show={show} onHide={closeFunction} placement='bottom' className='h-50'>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Voucher Activation</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {requesting ? (
          <RequestingAlert />
        ) : (
          <>
            {processing && <ProcessingAlert changeFunction={processed} />}

            {error.message != '' && (
              <Alert variant={error.severity}>
                <span>{error.message}</span>
              </Alert>
            )}
          </>
        )}

        <Formik
          validationSchema={schema}
          onSubmit={async (values, { resetForm }) => {
            await handleForm(values, { resetForm });
          }}
          initialValues={{
            activation_code: '',
          }}
        >
          {({ handleSubmit, handleChange, values, touched, errors }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group as={Row} className='mb-3' controlId='activation_code'>
                <Form.Label column sm={2}>
                  Activation Code
                </Form.Label>
                <Col sm={3}>
                  <Form.Control
                    type='text'
                    placeholder='Activation Code'
                    name='activation_code'
                    value={values.activation_code}
                    onChange={handleChange}
                    isInvalid={!!errors.activation_code}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.activation_code}
                  </Form.Control.Feedback>
                </Col>

                <Col sm={2}>
                  <Button variant='success' type='submit'>
                    Activate Now
                  </Button>
                </Col>
              </Form.Group>
            </Form>
          )}
        </Formik>
        <hr />
        <h6>Allocated vouchers</h6>
        {loading ? (
          <Alert variant='warning'>
            <Spinner animation='border' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </Spinner>
            <span>Vouchers loading...</span>
          </Alert>
        ) : (
          <Table striped>
            <thead>
              <tr>
                <th>Activation Code</th>
                <th>Expiry Date</th>
                <th>Quota</th>
                <th>Validity</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.length > 0 && (
                <>
                  {vouchers.map((voucher) => (
                    <tr key={voucher.activation_code}>
                      <td>{voucher.activation_code}</td>
                      <td>{Moment(voucher.expiry_date).format('YYYY-MM-DD')}</td>
                      <td>{voucher.quota_gb && <span>{voucher.quota_gb} GB</span>}</td>
                      <td>{voucher.validity_days && <span>{voucher.validity_days} days</span>}</td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </Table>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}

ActivateVoucher.propTypes = {
  show: PropTypes.bool,
  type: PropTypes.string,
  site: PropTypes.object,
  closeFunction: PropTypes.func,
};
